import { memo, useCallback, useEffect, useState } from 'react'
import { Box, FormControl, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { DropDownExpiries, setDropdownPastOptionExpiries } from '../../../../reducer/options.reducer'
import { selectPastOptionExpiriesV2, selectUnderlyingOptionsExpiriesV2 } from '../../../../selectors/options'
import { selectSnapshot } from '../../../../selectors/security'
import { findNearestNumber } from '../../../../util/tradepanel'
import { getSecurityAsync } from '../../../../util/security'
import { getOptionPastExpiries } from '../../../../services/apis/optionChain'
import { fetchOptionExpiriesV2 } from '../../../../util/options'
import { ExtraSmallButton, NonInteractiveTooltip } from '../../../../components/custom'
import { MULTIPANE_FULLSCREEN_MODE_ID } from '../../../../assets/constants'
import { selectMultiPaneFullScreen } from '../../../../selectors/common'
import ThinArrowRightIcon from '../../../../assets/icons/ThinArrowRightIcon'
import { useUser } from '../../../../context/UserContext'
import { FeatureCategory, FeatureName } from '../../../../components/adminUserRestrictions/Restrictions.constants'
import { Group, setFeatureRestrictionDialog } from '../../../../reducer/user.reducer'
import { FeatureCode } from '../../../../util/RestrictionFeatureCode'

export type OptionsExpiryMenuBodyProps = {
    underlyingToken: string
    expiryLabel: string
    onSelect: (expiry: DropDownExpiries, atmStrike: number) => void
    /** Hide past-expiry submenu (e.g. ATM option chart widget). */
    hidePastExpiry?: boolean
    /** Skip derived expiries in the list (ATM option chart). */
    hideDerivedInMenu?: boolean
    /** Use dropdown list without derived / near-month expiries. */
    excludeDerivedDropdown?: boolean
    buttonSuffix?: string
}

const OptionsExpiryMenuBody = ({
    underlyingToken,
    expiryLabel,
    onSelect,
    hidePastExpiry = false,
    hideDerivedInMenu = false,
    excludeDerivedDropdown = false,
    buttonSuffix
}: OptionsExpiryMenuBodyProps) => {
    const dispatch = useDispatch()
    const { user: { hasAccess } } = useUser()
    const [underlying, setUnderlying] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    const pastExpiries = useSelector(selectPastOptionExpiriesV2(underlyingToken))
    const optionsTradesData = useSelector(
        selectUnderlyingOptionsExpiriesV2(underlyingToken, excludeDerivedDropdown || hideDerivedInMenu)
    )
    const underlyingSnapshot = useSelector(selectSnapshot(underlyingToken))
    const [optionExpiryMenuAnchorEl, setOptionExpiryMenuAnchorEl] = useState<HTMLElement | null>(null)
    const [optionExpirySubmenuAnchorEl, setOptionExpirySubmenuAnchorEl] = useState<HTMLElement | null>(null)
    const container = useSelector(selectMultiPaneFullScreen)
    const hasPastExpiryAccess = hasAccess(FeatureCategory.CHART, '', FeatureName.PAST_EXPIRY)

    useEffect(() => {
        if (underlyingToken) {
            getSecurityAsync(underlyingToken).then(setUnderlying)
        } else {
            setUnderlying(undefined)
        }
    }, [underlyingToken])

    const fetchOptionsPastExpiries = useCallback(() => {
        if (!underlying) return
        getOptionPastExpiries({
            exchange: underlying.exchange,
            securityCode: underlying.code
        }).then((response) => {
            dispatch(
                setDropdownPastOptionExpiries({
                    token: underlyingToken,
                    optionExpiries: response
                })
            )
        })
    }, [dispatch, underlying, underlyingToken])

    const handleOptionExpiryMenuClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            if (!optionsTradesData?.length && underlying) {
                fetchOptionExpiriesV2(underlying, dispatch)
            }
            setOptionExpiryMenuAnchorEl(event.currentTarget)
        },
        [optionsTradesData?.length, underlying, dispatch]
    )

    const handleOptionExpiryMenuClose = useCallback(() => {
        setOptionExpiryMenuAnchorEl(null)
        setOptionExpirySubmenuAnchorEl(null)
    }, [])

    const openOptionExpirySubmenu = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            if (!hasPastExpiryAccess) {
                dispatch(
                    setFeatureRestrictionDialog({
                        open: true,
                        feature: 'Past expiries',
                        group: Group.CHART,
                        featureRestrictionCode: FeatureCode.PAST_EXPIRY
                    })
                )
                return
            }
            if (!pastExpiries) {
                fetchOptionsPastExpiries()
            }
            setOptionExpirySubmenuAnchorEl(event.currentTarget)
        },
        [hasPastExpiryAccess, pastExpiries, fetchOptionsPastExpiries, dispatch]
    )

    const buttonText = buttonSuffix && expiryLabel ? `${expiryLabel}(${buttonSuffix})` : expiryLabel

    if (!underlyingToken || !expiryLabel) return null

    return (
        <FormControl sx={{ flexShrink: 0 }}>
            <NonInteractiveTooltip placement="top" title="Change Expiry" arrow>
                <ExtraSmallButton
                    size="small"
                    variant="text"
                    onClick={handleOptionExpiryMenuClick}
                    color="inherit"
                    sx={{ padding: 0, margin: 0, display: 'inline-block', maxHeight: '24px', fontSize: '14px', minWidth: 0 }}
                >
                    {buttonText}
                </ExtraSmallButton>
            </NonInteractiveTooltip>
            {Boolean(optionExpiryMenuAnchorEl) && (
                <Menu
                    MenuListProps={{
                        onMouseLeave: handleOptionExpiryMenuClose
                    }}
                    container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
                    anchorEl={optionExpiryMenuAnchorEl}
                    open={Boolean(optionExpiryMenuAnchorEl)}
                    autoFocus
                    onClose={handleOptionExpiryMenuClose}
                >
                    {optionsTradesData?.map((o, i) => {
                        if (hideDerivedInMenu && o?.derived) return null
                        const atmStrike = findNearestNumber(o.strikesArray, underlyingSnapshot?.lastTradedPrice)
                        return (
                            <MenuItem
                                key={`${o.expiry.name}-${i}`}
                                value={o.expiry.name}
                                selected={o.expiry.name === expiryLabel}
                                onClick={() => {
                                    onSelect(o as DropDownExpiries, atmStrike ?? 0)
                                    handleOptionExpiryMenuClose()
                                }}
                            >
                                {o.expiry.name}
                            </MenuItem>
                        )
                    })}
                    {!hidePastExpiry && (
                        <Box component="span" sx={{ display: 'contents' }}>
                            <MenuItem
                                onClick={openOptionExpirySubmenu}
                                selected={pastExpiries?.some((ex) => ex.expiry.name === expiryLabel)}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <ListItemText sx={{ opacity: 1 }}>Past Expiry</ListItemText>
                                    <ListItemIcon sx={{ minWidth: 24 }}>
                                        <ThinArrowRightIcon fontSize="small" />
                                    </ListItemIcon>
                                </Box>
                            </MenuItem>
                            <Menu
                                MenuListProps={{
                                    onMouseLeave: () => setOptionExpirySubmenuAnchorEl(null)
                                }}
                                anchorEl={optionExpirySubmenuAnchorEl}
                                open={Boolean(optionExpirySubmenuAnchorEl)}
                                onClose={handleOptionExpiryMenuClose}
                                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            >
                                {pastExpiries?.map((dropdownExpiry: DropDownExpiries) => (
                                    <MenuItem
                                        key={dropdownExpiry.expiry.name}
                                        selected={dropdownExpiry.expiry.name === expiryLabel}
                                        value={dropdownExpiry.expiry.name}
                                        onClick={() => {
                                            onSelect(dropdownExpiry, 0)
                                            handleOptionExpiryMenuClose()
                                        }}
                                    >
                                        {dropdownExpiry.expiry.name}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>
                    )}
                </Menu>
            )}
        </FormControl>
    )
}

export default memo(OptionsExpiryMenuBody)
